// Weekly review: one block per active practice, four answers (keep,
// minimal, later, stop), no judgment language on a low count. The review
// can be parked; nothing expires and nothing resets in the meantime.
import type { Descriptor } from "../shell/descriptor.js";
import { weeklyTarget } from "../../shared/cadence.js";
import { isoWeekOf } from "../../shared/dates.js";
import { Avatar, avatarTitle } from "../shell/Avatar.js";
import { doseNote, useExpert, weekDates, WeekStrip, YOURS_AVATAR } from "../shell/fragments.js";
import { artFor } from "../shell/placeholderArt.js";
import { useStore, type AppStore } from "../state/store.js";
import type { PlanItemView } from "../state/types.js";
import { artImg } from "../assets.js";

// What the week actually says about one practice (31 Jul 2026, Rob). Two
// things the old "N of 7 days" got wrong: it measured every practice against
// seven whatever its cadence asked for, and it measured a week that has not
// happened yet. Three of three on a "3 x per week" habit is the whole thing
// done; five of five on the Friday of an unfinished week is too. A practice
// is only ever short of days that have already passed, and a few-times-a-week
// kind is not short at all while there are days left to do it in.
const SMALL = ["no", "one", "two", "three", "four", "five", "six", "seven"];

function daysWord(n: number): string {
  return `${SMALL[n] ?? n} ${n === 1 ? "day" : "days"}`;
}

const RETHINK = "Worth asking if it fits your life right now, and \u201cno\u201d is allowed.";

export function reviewNote(done: number, target: number, elapsed = 7): string {
  const left = Math.max(0, 7 - elapsed);
  if (target === 0) {
    return done === 0
      ? "Only as needed, and it was not needed this week."
      : `Done ${done === 1 ? "once" : `${done} times`} this week, and it is only for when you need it.`;
  }
  // A daily kind is measured against the days that have happened, never
  // against the ones still to come.
  if (target === 7) {
    if (done >= elapsed) {
      return elapsed === 7
        ? "Every day this week. That is the practice."
        : `${done} of ${elapsed} so far this week. That is the practice.`;
    }
    if (done === 0) return `Asked for every day, and nothing yet this week. ${RETHINK}`;
    return `${done} of ${elapsed} so far this week, ${SMALL[elapsed - done] ?? elapsed - done} short. That is traction, not failure.`;
  }
  if (done >= target) return `${done} of ${target} this week. That is the practice.`;
  // Still time to finish it, so this is a count, not a shortfall.
  if (left > 0) {
    return done === 0
      ? `Asked for ${target} times a week, nothing yet, and ${daysWord(left)} to go.`
      : `${done} of ${target} this week, with ${daysWord(left)} to go.`;
  }
  if (done === 0) return `Asked for ${target} times a week, and this week it did not happen. ${RETHINK}`;
  return `${done} of ${target} this week, ${SMALL[target - done] ?? target - done} short. That is traction, not failure.`;
}

function ReviewBlock({ item }: { item: PlanItemView }) {
  const s = useStore();
  const card = s.appState?.cards[item.cardId];
  const e = useExpert(card?.expertId ?? "");
  if (!card || !s.appState) return null;
  const yours = card.origin === "user";
  const dose = doseNote(card, item.chosenVersion);
  const target = weeklyTarget(card.cadence);
  // Days of this week that have actually happened, today included.
  const elapsed = weekDates(s.appState.today).indexOf(s.appState.today) + 1;
  return (
    // The practice's art leads the row and the person it came from is named
    // under the title (31 Jul 2026, Rob): a review is a week of your own
    // rows, and a wall of undifferentiated text is hard to place. The art is
    // the same one the card carries everywhere else, its topic's stand-in
    // when it has none of its own.
    <div className="rvrow">
      {/* The person rides on the art (31 Jul 2026, Rob), the way the byline
          sits on a hero: one object to look at rather than a picture and a
          face competing in two columns. */}
      <span className="rvart">
        <img
          className="rvimg"
          alt=""
          loading="lazy"
          /* Read off app.css, not guessed (Copilot, 3 Aug 2026): .rvart is a
             240px column, 176px under 1000, and full width once the card
             stacks at 520. */
          sizes="(max-width: 520px) 100vw, (max-width: 1000px) 176px, 240px"
          {...artImg(artFor(card))}
        />
        <span className="rvbyline">
          <Avatar e={yours ? YOURS_AVATAR : e} size="s" title={yours ? undefined : avatarTitle(e)} />
          <b>{yours ? "Yours" : e.name}</b>
        </span>
      </span>
      <div className="rvbody">
        {/* The title opens the practice, so remembering what a block is about
            never needs a separate link. */}
        <button
          className="rvtitle"
          onClick={() => s.openSheet({ kind: "practice", cardId: card.id })}
        >
          {card.do}
        </button>
        <WeekStrip planItemId={item.id} week={item.week} today={s.appState.today} />
        <p className="rvdays">
          {dose ? `${dose}. ` : ""}
          {reviewNote(item.week.length, target, elapsed)}
        </p>
        <div className="btnrow">
          {/* Left aligned, its natural width (31 Jul 2026, Rob): stretched
              across the row it read as the answer the screen wanted. */}
          <button
            className="btn"
            onClick={() =>
              s.openPopover({
                kind: "note",
                title: "Kept as is",
                body: "Nothing changed. That is a real answer too, and the week keeps counting.",
              })
            }
          >
            Keep as is
          </button>
          {item.chosenVersion !== "minimal" && card.versionMinimal && (
            <button className="btn quiet" onClick={() => void s.setPlanVersion(item.id, "minimal")}>
              Drop to minimal
            </button>
          )}
          <button
            className="btn quiet"
            onClick={(ev) => {
              const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
              s.openPopover({ kind: "snooze", cardId: card.id, x: r.right, y: r.bottom, planItemId: item.id });
            }}
          >
            Later ▾
          </button>
          <button className="btn quiet" onClick={() => void s.setPlanStatus(item.id, "stopped")}>
            Stop
          </button>
        </div>
      </div>
    </div>
  );
}

export function review(state: AppStore): Descriptor {
  const s = state.appState;
  // Parked for this ISO week only: next Sunday it simply comes back.
  const weekKey = s ? isoWeekOf(s.today) : "";
  const parked = (() => {
    try {
      return Boolean(weekKey) && localStorage.getItem("practice-review-parked") === weekKey;
    } catch {
      return false;
    }
  })();

  const park = (on: boolean) => {
    try {
      if (on) localStorage.setItem("practice-review-parked", weekKey);
      else localStorage.removeItem("practice-review-parked");
    } catch {
      // Parking is a convenience; failing quietly is fine.
    }
    useStore.setState({});
  };

  const list = (s?.plan ?? []).filter((p) => p.status === "active");

  const main = !s ? (
    <section className="panel glass">
      <p className="empty">Loading…</p>
    </section>
  ) : parked ? (
    <>
      <div className="notice info">
        <span className="ni">◷</span>
        <span>
          <b>Parked until next Sunday.</b> The review waits for you. Nothing expires and nothing resets in the meantime.
        </span>
      </div>
      <div className="btnrow" style={{ marginTop: 14 }}>
        <button className="btn solid" onClick={() => park(false)}>
          Do it now anyway
        </button>
        <button className="btn quiet" onClick={() => state.nav("today")}>
          Back to Today
        </button>
      </div>
    </>
  ) : (
    <section className="panel glass">
      {list.length ? (
        list.map((item) => <ReviewBlock key={item.id} item={item} />)
      ) : (
        <p className="empty">Nothing active to review. Your practices appear here once you add some.</p>
      )}
      {list.length > 0 && (
        <button className="btn blockbtn" style={{ marginTop: 16 }} onClick={() => park(true)}>
          Park the review until next Sunday
        </button>
      )}
    </section>
  );

  // No rail (31 Jul 2026, Rob): the review is one column of your own week,
  // and the screen takes the width back. The rule it used to carry is what
  // the copy in the blocks already says, and the export promise lives on its
  // own screen under Topic controls.
  return {
    nav: "review",
    title: "Weekly review",
    main,
  };
}
